import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useObraPendencias } from '@hooks/useObraPendencias.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { Button } from '@components/primitives/Button.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { NavIcon } from '@components/layout/NavIcons.js'
import {
  CardListSkeleton,
  FilterRowSkeleton,
  PageHeaderSkeleton,
} from '@components/primitives/Skeleton.js'
import { PendenciaCard } from '@components/pendencias/PendenciaCard.js'
import { PendenciaFormModal } from '@components/pendencias/PendenciaFormModal.js'
import { PendenciasIntegracaoSection } from '@components/pendencias/PendenciasIntegracaoSection.js'
import { ROUTES } from '@utils/constants.js'
import type { ObraPendenciaEnriquecida } from '@hooks/useObraPendencias.js'

export function PendenciasPage() {
  const navigate = useNavigate()
  const { itens, filtros, setFiltros, stats, isLoading, criar, editar, mudarStatus, remover } =
    useObraPendencias()

  const [modalOpen, setModalOpen] = useState(false)
  const [editando, setEditando] = useState<ObraPendenciaEnriquecida | null>(null)

  const abrirNova = useCallback(() => {
    setEditando(null)
    setModalOpen(true)
  }, [])

  const abrirEditar = useCallback((item: ObraPendenciaEnriquecida) => {
    setEditando(item)
    setModalOpen(true)
  }, [])

  const fecharModal = useCallback(() => {
    setModalOpen(false)
    setEditando(null)
  }, [])

  if (isLoading) {
    return (
      <PageShell>
        <PageHeaderSkeleton label='Carregando pendências…' />
        <section className='cf3-block'>
          <FilterRowSkeleton />
          <CardListSkeleton count={3} label='Carregando pendências…' />
        </section>
      </PageShell>
    )
  }

  const subtitleParts: string[] = []
  if (stats.abertas > 0) {
    subtitleParts.push(`${stats.abertas} aberta${stats.abertas !== 1 ? 's' : ''}`)
  }
  if (stats.altaPrioridade > 0) {
    subtitleParts.push(`${stats.altaPrioridade} alta prioridade`)
  }
  const subtitle =
    subtitleParts.length > 0
      ? `${subtitleParts.join(' · ')}. O que falta resolver no canteiro.`
      : 'Sua lista do canteiro. Checklists do guia e alertas do app ficam em outras telas.'

  return (
    <PageShell>
      <ModePageHeader title='Tarefas do canteiro' subtitle={subtitle}>
        <Button
          variant='primary'
          size='sm'
          className='cf3-section-hero__action'
          onClick={abrirNova}
        >
          Nova tarefa
        </Button>
      </ModePageHeader>

      <section className='cf3-block' aria-labelledby='pend-lista-heading'>
        <h2 id='pend-lista-heading' className='cf-sr-only'>
          Lista de tarefas
        </h2>

        <div className='cf3-pend-filters'>
          <label className='cf3-pend-filter'>
            <span className='cf-label'>Buscar</span>
            <input
              type='search'
              className='cf-input'
              placeholder='Título, responsável ou fase…'
              value={filtros.busca}
              onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))}
            />
          </label>
          <label className='cf3-pend-filter'>
            <span className='cf-label'>Status</span>
            <select
              className='cf-input'
              value={filtros.status}
              onChange={(e) =>
                setFiltros((f) => ({
                  ...f,
                  status: e.target.value as typeof filtros.status,
                }))
              }
            >
              <option value='ativas'>Abertas e em andamento</option>
              <option value='resolvidas'>Resolvidas</option>
              <option value='todas'>Todas</option>
            </select>
          </label>
          <label className='cf3-pend-filter'>
            <span className='cf-label'>Prioridade</span>
            <select
              className='cf-input'
              value={filtros.prioridade}
              onChange={(e) =>
                setFiltros((f) => ({
                  ...f,
                  prioridade: e.target.value as typeof filtros.prioridade,
                }))
              }
            >
              <option value=''>Todas</option>
              <option value='alta'>Alta</option>
              <option value='media'>Média</option>
              <option value='baixa'>Baixa</option>
            </select>
          </label>
        </div>

        {itens.length === 0 ? (
          <EmptyPanel
            icon={<NavIcon icon='pendencias' size={24} />}
            title='Nenhuma tarefa'
            description='Combinado com o pedreiro, compra com prazo, item de vistoria. Diário registra o dia. Aqui fica o que ainda precisa de ação.'
            actionLabel='Criar tarefa'
            onAction={abrirNova}
          />
        ) : (
          <>
            <p className='cf3-pend-lista__count' aria-live='polite'>
              {itens.length === stats.total
                ? `${stats.total} pendência${stats.total !== 1 ? 's' : ''}`
                : `${itens.length} de ${stats.total} pendências`}
            </p>
            <div className='cf3-pend-list'>
              {itens.map((item) => (
                <PendenciaCard
                  key={item.id}
                  item={item}
                  onMudarStatus={mudarStatus}
                  onEditar={abrirEditar}
                  onRemover={remover}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <PendenciasIntegracaoSection />

      <div className='cf3-block__card cf3-insight'>
        <p className='cf3-insight__text'>
          Três listas diferentes.{' '}
          <button
            type='button'
            className='cf3-insight__link'
            onClick={() => navigate(ROUTES.CHECKLIST_PENDENTES)}
          >
            Checklists do guia
          </button>{' '}
          são itens da jornada.{' '}
          <button
            type='button'
            className='cf3-insight__link'
            onClick={() => navigate(ROUTES.ALERTAS)}
          >
            Alertas
          </button>{' '}
          o app gera sozinho. Aqui você anota o combinado do canteiro.
        </p>
      </div>

      <PendenciaFormModal
        open={modalOpen}
        initial={editando}
        onClose={fecharModal}
        onSalvar={criar}
        onEditar={editar}
      />
    </PageShell>
  )
}
